import { useCallback, useState, type FormEvent } from 'react'
import { useRouter } from '@tanstack/react-router'
import { ArrowRight, ArrowUp, CheckCircle2, Sparkles, UtensilsCrossed, Wand2, X } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useCurrency } from '@/contexts/CurrencyContext'
import { useAICopilotGate } from '@/hooks/useAICopilotGate'
import { useOwnerBrief } from '@/hooks/useOwnerBrief'
import { useMenuReview } from '@/hooks/useMenuReview'
import { openCopilot } from '@/components/ai/copilotBus'
import { getNavTarget } from '@/lib/aiNavTargets'
import { knownRoutesFromRouter, runUiAction } from '@/lib/aiUiActions'
import { cn } from '@/lib/utils'
import { compactCurrency, formatCount, formatTrendPercent, trendDirection } from './dashboardFormat'
import { briefDateLabel, briefRows, type BriefFlag, type BriefHeadline } from './dailyBriefRows'
import type { AIOwnerBrief } from '@/types/ai'

// ── The owner's daily brief, at the top of the admin dashboard (F-AI-49) ────
// Yesterday's closed day in four numbers, one row per finding with a link to
// the screen that explains it, and a box that asks the copilot about today.
// The data is the same ai_digests row the bubble shows, through useOwnerBrief,
// so dismissing it here dismisses it there. Renders nothing when the copilot
// gate is off (the chips need the bubble), for non-admins, or after dismiss.

const ASK_PLACEHOLDER = 'Ask about today… (sales, stock, expenses)'

function HeadlineTile({ h, currencyCode }: { h: BriefHeadline; currencyCode: string }) {
  const value = h.isMoney ? compactCurrency(h.value, currencyCode) : formatCount(h.value)
  const pct = formatTrendPercent(h.deltaPct)
  const dir = trendDirection(h.deltaPct, h.goodWhenUp ? 'positive' : 'negative')
  const deltaText =
    h.delta === null
      ? null
      : pct
        ? pct
        : h.delta === 0
          ? 'no change'
          : `${h.delta > 0 ? '+' : '-'}${h.isMoney ? compactCurrency(Math.abs(h.delta), currencyCode) : formatCount(Math.abs(h.delta))}`
  return (
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{h.label}</p>
      <p className={cn('truncate text-lg font-semibold tabular-nums', h.key === 'net_profit' && h.value < 0 && 'text-destructive')}>
        {value}
      </p>
      {deltaText && (
        <p
          className={cn(
            'text-xs tabular-nums',
            dir === 'up' && 'text-emerald-600',
            dir === 'down' && 'text-destructive',
            (dir === 'flat' || dir === 'unknown') && 'text-muted-foreground',
          )}
        >
          {deltaText} vs day before
        </p>
      )}
    </div>
  )
}

/** "3 items" / "short Rs 350" / "Rs 1,200": the figure behind a finding, or null. */
function flagDetail(flag: BriefFlag, currencyCode: string): string | null {
  if (flag.kind === 'cash_variance' && typeof flag.amount === 'number') {
    return `${flag.amount < 0 ? 'short' : 'over'} ${compactCurrency(Math.abs(flag.amount), currencyCode)}`
  }
  if (typeof flag.amount === 'number' && flag.amount > 0) return compactCurrency(flag.amount, currencyCode)
  if (typeof flag.count === 'number' && flag.count > 0) {
    return `${formatCount(flag.count)} ${flag.kind === 'fiscal' ? 'invoice' : 'item'}${flag.count === 1 ? '' : 's'}`
  }
  return null
}

function FlagRow({
  flag,
  currencyCode,
  onView,
  onAsk,
}: {
  flag: BriefFlag
  currencyCode: string
  onView?: () => void
  onAsk: () => void
}) {
  const detail = flagDetail(flag, currencyCode)
  return (
    <li className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
      <span className="min-w-0 flex-1 truncate text-sm">
        {flag.label}
        {detail && <span className="ml-2 text-xs text-muted-foreground">{detail}</span>}
      </span>
      {onView && (
        <Button type="button" variant="ghost" size="sm" className="shrink-0 text-xs" onClick={onView}>
          View
          <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
        </Button>
      )}
      <Button type="button" variant="outline" size="sm" className="shrink-0 text-xs" onClick={onAsk}>
        <Sparkles className="mr-1 h-3 w-3 text-primary" aria-hidden />
        Ask
      </Button>
    </li>
  )
}

/**
 * F-AI-53: last month's menu review, three suggestions with a View and an Ask
 * each, under the daily brief. Same gate and dismiss flow as the brief.
 */
function MenuReviewCard({
  review,
  onView,
  onAsk,
  onFix,
  onDismiss,
}: {
  review: AIOwnerBrief
  onView: (target: string) => void
  onAsk: (prompt: string) => void
  /** F-AI-56: a one-tap fix — the copilot prepares a change card to confirm. */
  onFix: (prompt: string, send: boolean) => void
  onDismiss: () => void
}) {
  const m = (review.metrics ?? {}) as {
    month?: string
    suggestions?: {
      kind?: string
      label: string
      prompt: string
      target?: string
      fix?: { label: string; prompt: string; send: boolean }
    }[]
    uncosted_items?: number
  }
  const suggestions = (m.suggestions ?? []).filter((s) => s && s.label && s.prompt)
  return (
    <Card aria-label="Menu review" className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <UtensilsCrossed className="h-4 w-4 text-primary" aria-hidden />
          <p className="text-sm font-semibold">Menu review</p>
          {m.month && <p className="text-xs text-muted-foreground">{m.month}</p>}
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss menu review"
            className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {suggestions.length === 0 ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
            Nothing stands out this month.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {suggestions.map((s) => (
              <li key={s.label} className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
                <span className="min-w-0 flex-1 truncate text-sm">{s.label}</span>
                {s.target && (
                  <Button type="button" variant="ghost" size="sm" className="shrink-0 text-xs" onClick={() => onView(s.target!)}>
                    View
                    <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
                  </Button>
                )}
                <Button type="button" variant="outline" size="sm" className="shrink-0 text-xs" onClick={() => onAsk(s.prompt)}>
                  <Sparkles className="mr-1 h-3 w-3 text-primary" aria-hidden />
                  Ask
                </Button>
                {s.fix && (
                  <Button
                    type="button"
                    size="sm"
                    className="shrink-0 text-xs"
                    onClick={() => s.fix && onFix(s.fix.prompt, s.fix.send)}
                  >
                    <Wand2 className="mr-1 h-3 w-3" aria-hidden />
                    {s.fix.label}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {!!m.uncosted_items && (
          <p className="mt-2 text-xs text-muted-foreground">
            {m.uncosted_items} sold item{m.uncosted_items === 1 ? '' : 's'} have no cost record, so their margins are unknown. Add recipes under Menu → Costing.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function DailyBriefPanel() {
  const gate = useAICopilotGate()
  const { brief, isLoading, markSeen } = useOwnerBrief(gate.userId, gate.show ? gate.role : '')
  const menu = useMenuReview(gate.userId, gate.show ? gate.role : '')
  const { currencyCode } = useCurrency()
  const router = useRouter()
  const [question, setQuestion] = useState('')

  // Same path as a copilot ui_action: the registry's own route and search,
  // checked against the role, the flags and the router before navigating.
  const view = useCallback(
    (id: string) => {
      const t = getNavTarget(id)
      if (!t) return
      void runUiAction(
        router,
        { type: 'navigate', target: id, route: t.route, search: { ...t.search } },
        { role: gate.role, flags: gate.flags, knownRoutes: knownRoutesFromRouter(router) },
      ).then((r) => {
        if (r.outcome === 'refused') console.warn('[bhookly-ai] brief link refused', id, r.reason)
      })
    },
    [router, gate.role, gate.flags],
  )

  const ask = useCallback((prompt: string, send: boolean) => openCopilot({ seed: prompt, send }), [])

  const submit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      const text = question.trim()
      if (!text) return
      setQuestion('')
      ask(text, true)
    },
    [question, ask],
  )

  if (!gate.show || gate.role !== 'admin') return null

  if (isLoading) {
    return (
      <Card aria-busy="true" aria-label="Daily brief loading">
        <CardContent className="p-4">
          <Skeleton className="h-5 w-40" />
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  const rows = briefRows(brief)
  const showBrief = !!brief && !brief.seen && !!rows
  const showMenu = !!menu.review && !menu.review.seen
  if (!showBrief && !showMenu) return null

  const menuCard = showMenu && menu.review && (
    <MenuReviewCard
      review={menu.review}
      onView={view}
      onAsk={(p) => ask(p, true)}
      onFix={ask}
      onDismiss={() => menu.markSeen(menu.review!.id)}
    />
  )
  if (!showBrief || !brief || !rows) return <>{menuCard}</>

  return (
    <>
    <Card aria-label="Daily brief" className="border-primary/30">
      <CardContent className="p-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden />
          <p className="text-sm font-semibold">Yesterday at a glance</p>
          <p className="text-xs text-muted-foreground">
            {briefDateLabel(rows.businessDate)}
            {rows.comparedTo && ` · vs ${briefDateLabel(rows.comparedTo)}`}
          </p>
          <button
            type="button"
            onClick={() => markSeen(brief.id)}
            aria-label="Dismiss daily brief"
            className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {rows.headlines.map((h) => (
            <HeadlineTile key={h.key} h={h} currencyCode={currencyCode} />
          ))}
        </div>
        {rows.lowCoverage && (
          <p className="mt-2 text-xs text-muted-foreground">Cost data is incomplete — treat profit as a rough figure.</p>
        )}

        {rows.allClear ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden />
            All clear — no red flags.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {rows.flags.map((f) => (
              <FlagRow
                key={f.kind}
                flag={f}
                currencyCode={currencyCode}
                onView={f.target ? () => view(f.target!) : undefined}
                onAsk={() => ask(f.prompt, true)}
              />
            ))}
          </ul>
        )}

        <form onSubmit={submit} className="mt-3 flex items-center gap-2">
          <Input
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={ASK_PLACEHOLDER}
            aria-label="Ask Bhookly AI about today"
          />
          <Button type="submit" size="sm" disabled={!question.trim()} aria-label="Ask">
            <ArrowUp className="h-4 w-4" aria-hidden />
          </Button>
        </form>
      </CardContent>
    </Card>
    {menuCard}
    </>
  )
}
